/**
 * The conversation COLUMN — the scroller, the follow-the-stream behaviour, the jump pill and the
 * empty state — shared by every transcript host.
 *
 * Extracted from `WeaveTranscript` unchanged when the lane-v2 session became a second host of the
 * same conversation UX: the scroller markup, the `ResizeObserver` follow logic and the pill are the
 * transcript's behaviour, not the run's, so a host that reads `laneV2SessionStore` needs exactly
 * this and nothing from `runStateStore`. `WeaveTranscript` (v1, run-keyed) and `LaneV2Transcript`
 * (session-keyed) both mount it; neither carries a copy.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { ChevronDown, Cloud } from "lucide-react";
import { SAMPLE_PROMPTS, starterFill } from "@/constants/multi-agent/prompt.constants";

export interface FollowScroll {
  scrollerRef: RefObject<HTMLDivElement>;
  contentRef: RefObject<HTMLDivElement>;
  showJump: boolean;
  scrollToBottom: (smooth?: boolean) => void;
}

export type FollowMode =
  /** Follow the live edge: a new run re-arms following and the column tracks growth at the bottom. */
  | "follow"
  /**
   * The newest exchange owns the viewport: on a new key the element marked `data-weave-pin` is
   * scrolled to the TOP and growth is NOT followed — the answer grows downward under the question.
   * For an agentic turn whose answer arrives whole and whose activity narrates in place, following
   * the bottom would scroll the question (and the story under it) away while the reader waits.
   */
  | "pin";

/**
 * ── FOLLOW THE STREAM, AND STOP WHEN THE READER TAKES OVER ──────────────────────────────────
 *
 * Scrolling someone back down while they are reading an earlier turn is the single most
 * infuriating behaviour a streaming transcript can have — and the version this replaces did it,
 * despite looking like it could not.
 *
 * It re-derived "is the reader at the bottom" from `scrollHeight - scrollTop - clientHeight < 64`
 * on every scroll event. A streaming answer CHANGES `scrollHeight` continuously, and each
 * programmatic jump fires another scroll event — so a reader who scrolled up a little got
 * re-pinned by the next frame that happened to land within the threshold, and was yanked back
 * down. The distance is an effect of scrolling; it is not the reader's intention.
 *
 * So intent was recorded from the INPUT DEVICE: a wheel-up, an upward touch drag or a Page/Home
 * key set `userScrolledUpRef`, and only the reader returning to the bottom cleared it.
 *
 * ── WHY THAT WAS STILL WRONG, AND WHAT REPLACED IT ──────────────────────────────────────────
 *
 * REPORTED AGAIN, and reproduced in the harness: park the reader mid-conversation, stream one
 * more delta, and they are returned to the bottom (measured: parked at 2086px, next delta put
 * them at 3346px, 0px from the bottom).
 *
 * Listening for `wheel` and `touchmove` enumerates the ways a reader can scroll, and the list is
 * incomplete. Dragging the scrollbar thumb fires a `scroll` event and no `wheel`. So do clicking
 * the scrollbar track, autoscroll from dragging a selection past the edge, find-in-page,
 * middle-click autoscroll, and `scrollIntoView` from a link. Every one of those left the flag
 * false — so the transcript believed the reader was still following and re-pinned them. The
 * `keydown` listener has a second hole: it is bound to the scroller, which has no `tabIndex`, so
 * Page/Home only arrive when focus is already inside the transcript — never while the reader is
 * typing in the composer.
 *
 * POSITION IS THE ANSWER AFTER ALL — the note above rejected it for a reason that does not hold:
 *
 *   · "a streaming answer changes `scrollHeight` continuously" — and changing it fires NO scroll
 *     event. Growth cannot move `following`, because only a real scroll runs the handler.
 *   · "each programmatic jump fires another scroll event" — and every jump this component makes
 *     lands AT THE BOTTOM, which is precisely the position that means "following". Our own scroll
 *     therefore sets the flag to the value it already had. There is nothing to guard against.
 *
 * The one exception is the pill's SMOOTH scroll, which passes through intermediate positions on
 * its way down; `settlingRef` covers exactly that, for exactly as long as it runs.
 *
 * ── AND GROWTH IS OBSERVED, NOT INFERRED FROM `messages` ────────────────────────────────────
 *
 * The follow-scroll used to hang off `[messages]`. That is the "messages changed → scrollToBottom"
 * shape, and it is wrong in both directions: `messages` gets a new identity on every
 * `message.part_delta` whether or not anything grew, while the things that DO change the height
 * without touching it — shiki painting a block, an image loading, Monaco mounting, the workspace
 * opening and reflowing the column — never triggered it at all. A `ResizeObserver` on the content
 * column asks the question directly: did this get taller, and is the reader following.
 *
 * `rearmKey`: A NEW CONVERSATION (or run) OVERRIDES THE SCROLL POSITION, and only that does. The
 * reader pressed Send; the one thing they are waiting for is about to appear at the bottom. Leaving
 * them parked three turns up — because they had scrolled there a minute ago — is the mirror of the
 * bug above, and legacy handled it by re-arming follow on every run start. This is also what covers
 * switching conversations: the key moves, so opening another chat lands at its newest message
 * rather than inheriting the last one's offset.
 */
export function useFollowScroll(rearmKey: string | null, mode: FollowMode = "follow"): FollowScroll {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  /** Is the reader at the live edge? Starts true: a conversation opens at its newest message. */
  const followingRef = useRef(true);
  /** A smooth jump is in flight; its intermediate positions are not the reader changing their mind. */
  const settlingRef = useRef(false);
  const [showJump, setShowJump] = useState(false);
  const frameRef = useRef(0);
  const settleRef = useRef(0);

  /** At the bottom, for "are we following". Generous: a small nudge is not a decision to leave. */
  const AT_BOTTOM_PX = 80;
  /** Far enough away that the reader has clearly left, and the pill is worth showing. */
  const SHOW_JUMP_PX = 120;
  /** Breathing room above a question pinned to the top. */
  const PIN_GAP_PX = 8;

  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollerRef.current;
    if (!el) return;
    cancelAnimationFrame(frameRef.current);
    // rAF so the jump lands AFTER the frame that grew the content; without it a streamed chunk
    // arrives a pixel below the scroll that was meant to reveal it.
    frameRef.current = requestAnimationFrame(() => {
      const node = scrollerRef.current;
      if (!node) return;
      if (smooth) {
        /*
         * The reader ASKED to come back, so following is true from this moment — not from whenever
         * the animation happens to land. Without this, a delta arriving mid-animation would read
         * the intermediate position as "they are away" and stop following again.
         */
        followingRef.current = true;
        settlingRef.current = true;
        window.clearTimeout(settleRef.current);
        settleRef.current = window.setTimeout(() => {
          settlingRef.current = false;
          const node2 = scrollerRef.current;
          // A smooth scroll aims at the height captured when it started. If the answer grew while
          // it was running it lands short, so the last word is an instant re-pin.
          if (node2 && followingRef.current) node2.scrollTop = node2.scrollHeight;
        }, 600);
        node.scrollTo({ top: node.scrollHeight, behavior: "smooth" });
      } else {
        node.scrollTop = node.scrollHeight;
      }
    });
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowJump(fromBottom > SHOW_JUMP_PX);
      // The ONLY writer of `following`, and it runs only on a real scroll — by any means, which is
      // the whole point. Growth never reaches here; our own instant jumps land at the bottom and
      // answer "true", which is what they already were.
      if (!settlingRef.current) followingRef.current = fromBottom <= AT_BOTTOM_PX;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frameRef.current);
      window.clearTimeout(settleRef.current);
    };
  }, []);

  /*
   * DID THE CONVERSATION GET TALLER? The one trigger for following the stream.
   *
   * Covers every source of growth at once — a streamed delta, a block that finished highlighting,
   * an image, the workspace opening and reflowing the column to a narrower width — because it
   * measures the outcome rather than guessing at the causes. The pill is refreshed here too: a
   * parked reader's distance from the bottom changes as the answer grows, and no scroll event fires
   * to tell them.
   */
  useEffect(() => {
    const el = scrollerRef.current;
    const content = contentRef.current;
    if (!el || !content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (followingRef.current && mode === "follow") scrollToBottom();
      const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowJump(fromBottom > SHOW_JUMP_PX);
    });
    observer.observe(content);
    return () => observer.disconnect();
  }, [scrollToBottom, mode]);

  useEffect(() => {
    if (!rearmKey) return;
    if (mode === "pin") {
      // The newest question to the top. rAF so the turn has laid out (its min-height is what lets
      // a question near the end of a long conversation actually reach the top).
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => {
        const el = scrollerRef.current;
        const pin = el?.querySelector<HTMLElement>("[data-weave-pin]");
        if (!el || !pin) return;
        // Measured against the scroller's own box: `offsetTop` is relative to the offsetParent, which this
        // `relative` scroller already is, so subtracting the scroller's offset under-shot by the masthead.
        el.scrollTop += pin.getBoundingClientRect().top - el.getBoundingClientRect().top - PIN_GAP_PX;
      });
      return;
    }
    followingRef.current = true;
    scrollToBottom();
  }, [rearmKey, scrollToBottom, mode]);

  return { scrollerRef, contentRef, showJump, scrollToBottom };
}

export interface TranscriptColumnProps {
  scroll: FollowScroll;
  /** Is anything still arriving? Only the jump pill's LABEL depends on this. */
  live: boolean;
  /**
   * A block that stays visible at the bottom of the column while the reader scrolls — the pending
   * decision, directly above the composer. Sticky inside the scroller rather than a portal, so it
   * is part of the conversation and never covers it.
   */
  dock?: ReactNode;
  /**
   * Overrides the scroller's own `flex-1 overflow-y-auto`. Any override should carry
   * `overflow-x-hidden` alongside its own `overflow-y`, for the reason on the default below — a
   * lone `overflow-y` gets `overflow-x` normalised to `auto` by the UA, not `visible`.
   */
  className?: string;
  children: ReactNode;
}

export function TranscriptColumn({ scroll, live, dock, className, children }: TranscriptColumnProps) {
  const { scrollerRef, contentRef, showJump, scrollToBottom } = scroll;
  /*
   * The dock is sticky at the bottom, so it OVERLAYS whatever is above it until its own place in
   * the flow scrolls into view. The conversation is therefore padded by the dock's height — measured,
   * because a decision card's height is the server's prompt plus the plan — so the last turn can
   * always be scrolled fully clear of it. Same mechanism the composer dock uses (`--dock-h`).
   */
  const dockRef = useRef<HTMLDivElement | null>(null);
  const [dockHeight, setDockHeight] = useState(0);
  useEffect(() => {
    const el = dockRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      setDockHeight(0);
      return;
    }
    const observer = new ResizeObserver(() => setDockHeight(el.offsetHeight));
    observer.observe(el);
    setDockHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, [dock]);
  /*
   * The scroller's own visible height, as `--ma-col-h` on the content. The newest turn reserves this much room
   * (`LaneV2Transcript`) so its question can reach the TOP of the column with the answer growing beneath it, the way
   * an editor's chat does. It was `100vh - 280px`, a guess ~130px short on a 732px window (owner 2026-11-02): the
   * new question stopped part-way down with the previous one still pinned above it.
   */
  useEffect(() => {
    const el = scrollerRef.current;
    const content = contentRef.current;
    if (!el || !content || typeof ResizeObserver === "undefined") return;
    const set = () => content.style.setProperty("--ma-col-h", `${el.clientHeight}px`);
    const observer = new ResizeObserver(set);
    observer.observe(el);
    set();
    return () => observer.disconnect();
  }, [scrollerRef, contentRef]);
  return (
    <div
      ref={scrollerRef}
      // A stable handle for the scroll checks. Selecting the scroller by its utility classes made a
      // styling change look like a behaviour regression, and picked a different element depending on
      // what else happened to be scrollable — same reasoning as `data-weave-user-turn`.
      data-weave-scroller=""
      /*
       * ── `overflow-x-hidden` IS EXPLICIT, NOT LEFT TO THE DEFAULT ─────────────────────────────
       *
       * This was `flex-1 overflow-y-auto` — one axis named, the other left unset. CSS requires the
       * two axes to agree on "visible or not": per spec, an element with `overflow-y: auto` and no
       * `overflow-x` does NOT compute `overflow-x` as `visible` — the UA normalises it to `auto`
       * too (verified directly: `getComputedStyle` on such an element reports `overflow-x: auto`).
       *
       * This scroller was only ever meant to scroll VERTICALLY — every wide thing inside it already
       * owns its own horizontal handling (`CollapsibleMarkdownTable`'s `overflow-auto` wrapper,
       * `ChatCodeBlock`'s capped `pre`, prose's `overflow-wrap: anywhere` on long unbroken strings,
       * §8.5). An implicit second scroll axis here is not a feature; it is a second place the SAME
       * overflow could surface a scrollbar, triggered by nothing more than the content sitting a
       * pixel or two past the scroller's own width — which is a state a subtle reflow (a wider or
       * narrower sibling panel changing how much room the scroller has) can produce without any
       * content looking visibly broken. `overflow-x-hidden` makes the "one axis only" intent the
       * actual, measured behaviour, the same fix applied to `ma-ws-body` in `WeaveWorkspace.tsx`.
       */
      className={`relative ${className ?? "flex-1 overflow-y-auto overflow-x-hidden"}`}
    >
      <div
        // Observed for height changes — the one trigger for following the stream. See the
        // `ResizeObserver` above for why growth is measured here rather than inferred from `messages`.
        ref={contentRef}
        /*
         * `gap-5` between turns: enough to separate two exchanges, not the 24px-plus-markdown-
         * margins that made one turn look like three unrelated fragments.
         *
         * ── TOP-ANCHORED, AND WHY THE BOTTOM ANCHOR HAD TO GO ─────────────────
         *
         * This was `min-h-full justify-end`, to keep a short conversation near the composer rather
         * than a viewport above it. It did that, and it also made every answer DRIFT: while the
         * content is shorter than the scroller, each new line pushes everything above it upward, so
         * the sentence a reader is in the middle of moves under them, then stops moving the moment
         * the content passes the container height. Measured in the harness: the same anchor sits at
         * 234px in a short answer and 36px in a long one — ~200px of travel through text that had
         * already been read. That is the "jumpy" a reviewer sees and cannot name.
         *
         * Top-anchored, growth extends DOWNWARD into empty space and nothing already read moves.
         * The original concern is answered by the scroll effect below, not by the layout: it follows
         * the live edge while the reader is at the bottom, which is what keeps the new content in
         * view. This is also the shape every assistant surface settled on — content from the top,
         * composer pinned, the view following the tail.
         */
        className="mx-auto flex w-full flex-col gap-7 py-5"
        /*
         * ── THE SAME SIDE EDGE AS THE COMPOSER, AND THAT IS THE POINT ───────────────────────────
         *
         * This was a flat `px-4`. The composer dock below uses `clamp(24px, 3vw, 48px)` — its own
         * comment says the two are meant to share one left/right edge — so the answer sat 8 to 32px
         * further out than the box the reader types into, and the text ran closer to the panel edge
         * the wider the window got. In the DEFAULT layout `--ma-prose-max` is `100%`
         * (`MultiAgentIdePanel` sets it), so `px-4` was the only thing between prose and the frame.
         *
         * Same expression rather than a shared token because it is one number in two files and a
         * token would hide which two; if a third surface ever needs it, that is the moment to name it.
         */
        /*
         * `--ma-prose-max` ALONE — the chat cap is the composer's, not this column's.
         *
         * Capping the conversation to the composer's width was overreach: it was asked for on the
         * INPUT, and applying it here shrank every answer, table and card to the measure as well.
         * The answer already caps its own prose at `--ma-answer-measure` (see `.ma-answer`), which
         * is the line-length lever; tables and cards legitimately want the whole column.
         */
        style={{ maxWidth: "var(--ma-prose-max)", paddingInline: "clamp(24px, 3vw, 48px)", paddingBottom: dock ? dockHeight + 8 : undefined }}
      >
        {children}
      </div>

      {dock && (
        <div
          ref={dockRef}
          data-weave-dock=""
          className="sticky bottom-0 z-10 mx-auto w-full border-t border-border/60 bg-background pb-2 pt-2"
          style={{ maxWidth: "var(--ma-prose-max)", paddingInline: "clamp(24px, 3vw, 48px)" }}
        >
          {dock}
        </div>
      )}

      {/*
        ── JUMP TO LATEST ──────────────────────────────────────────────────────────────────────

        Restored. Without it, a reader who scrolled up to check something had no way back down but
        to drag — and while an answer streams the target keeps moving, so dragging never quite
        lands. It is the other half of "follow on intent": the moment the transcript stops chasing
        the reader, the reader needs a way to opt back in.

        `position: sticky` in a full-width, `pointer-events-none` row, so the prose underneath stays
        selectable and clickable and the pill still parks itself against the right edge of the
        column as panels resize. `float`/`absolute` were both tried in the pre-migration panel and
        neither tracks a resizing column.
      */}
      {showJump && !dock && (
        <div className="pointer-events-none sticky bottom-4 z-10 flex shrink-0 justify-end pr-4">
          <button
            type="button"
            onClick={() => {
              // `scrollToBottom(true)` re-arms following itself — the reader asking to come back IS
              // the decision to follow again, and it has to hold for the whole animation.
              scrollToBottom(true);
            }}
            aria-label="Jump to the latest message"
            className="pointer-events-auto inline-flex h-8 min-h-6 items-center justify-center gap-1 rounded-full border border-border bg-card px-3 text-[11px] font-medium text-muted-foreground shadow-md transition-colors motion-reduce:transition-none hover:bg-muted/60 hover:text-foreground"
          >
            <ChevronDown aria-hidden className="h-4 w-4 shrink-0" />
            {/* The label appears only while something is actually arriving — otherwise the pill is
                a quiet affordance rather than an announcement. */}
            {live && <span>New messages</span>}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Before the first frame.
 *
 * Deliberately almost nothing: a skeleton of a conversation that has not started is a promise about
 * a shape nobody has asked for yet, and §10's rule is that a skeleton must reserve the FINAL
 * layout — which here is unknowable.
 */
export function EmptyConversation({ onAsk }: { onAsk?: (question: string) => void }) {
  return (
    <div className="flex flex-col items-center gap-5 py-10 text-center">
      <OrgOrbit />
      <div className="flex flex-col items-center gap-2">
        <p className="text-[14px] font-medium text-foreground">Ask Weave about your org</p>
        <p className="max-w-sm text-[13px] leading-snug text-muted-foreground">
          Ask a question, or describe a change. Weave proposes a plan before it touches your org.
        </p>
      </div>

      {/*
        REAL STARTING POINTS, not placeholder copy. The empty state said "describe the outcome you
        want" and left the reader to invent a first request from nothing, while legacy's own hero has
        always offered four concrete ones. Same list, from
        `constants/multi-agent/prompt.constants` — moved there so there is one copy, not two.

        Clicking fills the composer and stops. Nothing is sent until the reader sends it.
      */}
      {onAsk && (
        <div className="grid w-full max-w-lg gap-2 [grid-template-columns:repeat(auto-fit,minmax(min(220px,100%),1fr))]">
          {SAMPLE_PROMPTS.map((prompt) => (
            <button
              key={prompt.title}
              type="button"
              onClick={() => onAsk(starterFill(prompt))}
              className="flex min-w-0 flex-col items-start gap-1 rounded-lg border border-border bg-card px-3 py-2.5 text-left transition-colors motion-reduce:transition-none hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="w-full break-words text-[13px] font-medium text-foreground">
                {prompt.title}
              </span>
              <span className="text-[12px] text-muted-foreground">{prompt.lane}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The idle centrepiece: a cloud with four SObject tokens orbiting it.
 *
 * ── WHY THIS IS BACK ────────────────────────────────────────────────────────────────────────────
 *
 * The empty state had a 20px `Sparkles` glyph. That is the generic mark every AI product ships and
 * it says nothing about what this one does; the animation it replaced said "Salesforce org" before
 * a word was read, and was reported as the better of the two. It is the pre-migration hero's own
 * centrepiece, unchanged — the ring rotates, each token counter-rotates so its letter stays upright.
 *
 * Decoration, so `aria-hidden`: the heading below carries the meaning, and four letters read out as
 * "A C O Cs" would be noise. The keyframes already ship in `multi-agent.css`, where
 * `prefers-reduced-motion` stops both rotations — the glow and the hub pulse are paused here for
 * the same reason, in the same query.
 */
function OrgOrbit() {
  // Precomputed translations rather than trig at render: r = 70px on the four cardinal points.
  const ORBIT = [
    { x: 70, y: 0, label: "A", color: "hsl(212 84% 52%)", title: "Account" },
    { x: 0, y: 70, label: "C", color: "hsl(150 64% 40%)", title: "Contact" },
    { x: -70, y: 0, label: "O", color: "hsl(28 92% 50%)", title: "Opportunity" },
    { x: 0, y: -70, label: "Cs", color: "hsl(0 72% 51%)", title: "Case" },
  ];

  return (
    <div aria-hidden className="ma-orbit relative shrink-0" style={{ width: 180, height: 180 }}>
      <span
        className="ma-orbit-glow absolute inset-0"
        style={{
          borderRadius: 999,
          background:
            "radial-gradient(circle at center, color-mix(in oklab, var(--ma-accent) 38%, transparent), transparent 68%)",
          filter: "blur(6px)",
          animation: "ma-orbit-glow 3.2s ease-in-out infinite",
        }}
      />
      <span
        className="absolute"
        style={{
          inset: 19,
          borderRadius: 999,
          border: "1px dashed color-mix(in oklab, var(--ma-accent) 30%, transparent)",
        }}
      />
      <span
        className="ma-orbit-hub absolute inset-0 m-auto inline-flex items-center justify-center"
        style={{
          width: 64,
          height: 64,
          borderRadius: 999,
          background: "var(--ma-grad-accent)",
          color: "var(--ma-accent)",
          boxShadow: "var(--ma-shadow-md)",
          animation: "ma-pulse 2.8s ease-in-out infinite",
        }}
      >
        <Cloud className="h-7 w-7" />
      </span>
      <div className="ma-orbit-ring absolute inset-0" style={{ animation: "ma-orbit 22s linear infinite" }}>
        {ORBIT.map(({ x, y, label, color, title }) => (
          <span
            key={title}
            className="absolute"
            style={{
              top: "50%",
              left: "50%",
              width: 26,
              height: 26,
              marginTop: -13,
              marginLeft: -13,
              transform: `translate(${x}px, ${y}px)`,
            }}
          >
            <span
              className="ma-orbit-chip inline-flex h-full w-full items-center justify-center"
              style={{
                borderRadius: 7,
                background: color,
                color: "white",
                fontSize: 11,
                fontWeight: 700,
                boxShadow: "var(--ma-shadow-sm)",
                animation: "ma-orbit-rev 22s linear infinite",
              }}
            >
              {label}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
